import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw } from "lucide-react";
import type { LapComparisonLap, LapComparisonRole } from "@/types";
import { formatDateShort } from "@/utils/dateFormat";
import { formatLapTime } from "@/utils/timeFormat";
import {
  COMPARISON_COLOR,
  REFERENCE_COLOR,
  formatSignedSeconds,
  vehicleLabel,
} from "./comparisonMath";

export interface LapSlotFallback {
  sessionTitle?: string;
  driverName?: string;
  vehicleName?: string;
  conductedAt?: string;
  lapNumber?: number;
  lapTimeMs?: number;
}

interface LapSlotCardProps {
  role: LapComparisonRole;
  lap: LapComparisonLap | null;
  /** Context shown while the comparison itself is still loading. */
  fallback?: LapSlotFallback;
  /** Comparison-minus-reference lap time, shown on the comparison card. */
  deltaMs?: number | null;
  /** Picker cannot open (reference session unknown or unreadable). */
  disabled?: boolean;
  onReplace: () => void;
}

export default function LapSlotCard({
  role,
  lap,
  fallback,
  deltaMs,
  disabled = false,
  onReplace,
}: LapSlotCardProps) {
  const { t } = useTranslation("pages");
  const color = role === "reference" ? REFERENCE_COLOR : COMPARISON_COLOR;
  const roleLabel = t(`lapComparison.${role}`);

  const title = lap?.session.title ?? fallback?.sessionTitle;
  const driver = lap?.session.driver.name ?? fallback?.driverName;
  const vehicle = lap ? vehicleLabel(lap.session.vehicle) : fallback?.vehicleName;
  const conductedAt = lap?.session.conductedAt ?? fallback?.conductedAt;
  const lapNumber = lap?.lapNumber ?? fallback?.lapNumber;
  const lapTimeMs = lap?.lapTimeMs ?? fallback?.lapTimeMs;
  const hasLap = lapNumber !== undefined;

  return (
    <div
      className="flex min-w-0 flex-1 flex-col gap-2 rounded-xl border bg-[#0d0d16] p-3"
      style={{ borderColor: `${color}55` }}
      data-testid={`lap-slot-${role}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color }}>
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
          {roleLabel}
        </span>
        <button
          type="button"
          onClick={onReplace}
          disabled={disabled}
          title={disabled ? t("lapComparison.pickerUnavailable") : undefined}
          className="inline-flex items-center gap-1 rounded-md border border-[#1f2937] px-2 py-1 text-xs text-slate-300 hover:bg-[#111827] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          <RefreshCw className="h-3 w-3" />
          {hasLap ? t("lapComparison.replace") : t("lapComparison.choose")}
        </button>
      </div>

      {!hasLap ? (
        <button
          type="button"
          onClick={onReplace}
          disabled={disabled}
          title={disabled ? t("lapComparison.pickerUnavailable") : undefined}
          className="rounded-lg border border-dashed border-[#1f2937] px-3 py-5 text-center text-sm text-slate-400 hover:border-slate-500 hover:text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {role === "comparison"
            ? t("lapComparison.chooseComparison")
            : t("lapComparison.choose")}
        </button>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-100">
                {driver ?? "—"}
                {vehicle ? <span className="font-normal text-slate-400"> · {vehicle}</span> : null}
              </div>
              <div className="truncate text-xs text-slate-500">
                {title ?? ""}
                {conductedAt ? ` · ${formatDateShort(conductedAt)}` : ""}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="font-mono text-lg font-semibold text-slate-100">
                {lapTimeMs !== undefined ? formatLapTime(lapTimeMs) : "--:--.---"}
              </div>
              <div className="text-xs text-slate-500">
                {t("lapComparison.lapNumber", { number: lapNumber })}
              </div>
            </div>
          </div>
          <div className="flex min-h-[20px] items-center gap-2 text-xs">
            {lap && !lap.isValid && (
              <span className="inline-flex items-center gap-1 rounded bg-amber-950/60 px-1.5 py-0.5 text-amber-300">
                <AlertTriangle className="h-3 w-3" />
                {t("lapComparison.invalid")}
              </span>
            )}
            {role === "comparison" && deltaMs !== undefined && deltaMs !== null && (
              <span
                className={`font-mono font-semibold ${
                  deltaMs > 0 ? "text-red-300" : deltaMs < 0 ? "text-green-300" : "text-slate-300"
                }`}
                data-testid="lap-time-delta"
              >
                {formatSignedSeconds(deltaMs / 1000)}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
